/**
 * TokenManager - Manages JWT lifecycle for VetScheduler authentication
 *
 * Handles:
 * - Token issuance from backend
 * - Token storage in memory (never localStorage)
 * - Automatic token refresh before expiry
 * - Token validation
 */
class TokenManager {
  constructor() {
    this.token = null;
    this.expiresAt = null;
    this.isRefreshing = false;
    this.refreshBuffer = 30; // Refresh 30 seconds before expiry
    this.clinicKey = null;
    this.origin = null;
  }

  /**
   * Request a new token from the backend
   * @param {string} key - Clinic identifier
   * @param {string} origin - Current origin (window.location.origin)
   * @returns {Promise<string>} JWT token
   */
  async requestToken(key, origin) {
    try {
      // Store for refresh
      this.clinicKey = key;
      this.origin = origin;

      const response = await fetch('/api/embed/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key,
          origin,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get token');
      }

      const data = await response.json();

      // Store token and expiry
      this.token = data.token;
      this.expiresAt = data.issued_at + data.expires_in;

      return this.token;
    } catch (error) {
      console.error('❌ Error requesting token:', error);
      throw error;
    }
  }

  /**
   * Get a valid token, refreshing if needed
   * @returns {Promise<string>} Valid JWT token
   */
  async getToken() {
    // If no token, request one
    if (!this.token) {
      if (!this.clinicKey || !this.origin) {
        throw new Error('Token manager not initialized. Call requestToken() first.');
      }
      return this.requestToken(this.clinicKey, this.origin);
    }

    // Check if token is expired or near expiry
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = this.expiresAt - now;

    // If expired, refresh immediately
    if (timeUntilExpiry <= 0) {
      return this.refreshToken();
    }

    // If near expiry and not already refreshing, refresh in background
    if (timeUntilExpiry <= this.refreshBuffer && !this.isRefreshing) {
      // Don't await - refresh in background
      this.refreshToken().catch((error) => {
        console.error('Background token refresh failed:', error);
      });
    }

    return this.token;
  }

  /**
   * Force refresh the token
   * @returns {Promise<string>} New JWT token
   */
  async refreshToken() {
    // Prevent concurrent refresh requests
    if (this.isRefreshing) {
      // Wait for ongoing refresh
      await new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isRefreshing) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
      return this.token;
    }

    try {
      this.isRefreshing = true;

      if (!this.clinicKey || !this.origin) {
        throw new Error('Cannot refresh token: clinic key or origin not set');
      }

      await this.requestToken(this.clinicKey, this.origin);

      return this.token;
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Clear the token from memory
   */
  clearToken() {
    this.token = null;
    this.expiresAt = null;
    this.isRefreshing = false;
  }

  /**
   * Check if token is valid (exists and not expired)
   * @returns {boolean} True if token is valid
   */
  isTokenValid() {
    if (!this.token || !this.expiresAt) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return now < this.expiresAt;
  }
}

export default TokenManager;
